import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { Loader2, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full bg-transparent border-0 border-b border-charcoal/20 rounded-none px-0 py-3 text-lg font-light text-charcoal placeholder:text-stone/50 focus:outline-none focus:border-clay transition-colors";

export const BookingForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    horse_name: "",
    yard_location: "",
    preferred_date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setDone(true);
      toast.success("Request sent — I'll be in touch within 24–48 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="py-24 md:py-36 bg-sand-dark">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5">
            <Overline>Book a session</Overline>
            <h2 className="mt-5 font-heading text-4xl md:text-5xl font-light tracking-tight text-charcoal leading-tight">
              Request an appointment
            </h2>
            <p className="mt-6 text-stone text-lg font-light leading-relaxed">
              Share a few details about you and your horse, and I'll personally get back to you within 24–48 hours to confirm a time. A confirmation will be sent to your inbox.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            {done ? (
              <div
                data-testid="booking-success"
                className="bg-sand rounded-3xl p-10 border border-charcoal/5 text-center"
              >
                <CheckCircle2 className="text-clay mx-auto" size={48} strokeWidth={1.5} />
                <h3 className="mt-5 font-heading text-3xl text-charcoal">Request received</h3>
                <p className="mt-3 text-stone font-light">
                  Thank you, {form.name.split(" ")[0]}. I'll be in touch very soon to confirm your appointment.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="booking-form" className="flex flex-col gap-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <input data-testid="booking-name" className={field} placeholder="Your name *" value={form.name} onChange={update("name")} />
                  <input data-testid="booking-phone" className={field} placeholder="Phone *" value={form.phone} onChange={update("phone")} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <input data-testid="booking-email" type="email" className={field} placeholder="Email *" value={form.email} onChange={update("email")} />
                  <input data-testid="booking-horse" className={field} placeholder="Horse's name" value={form.horse_name} onChange={update("horse_name")} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <input data-testid="booking-yard" className={field} placeholder="Barn / location" value={form.yard_location} onChange={update("yard_location")} />
                  <input data-testid="booking-date" className={field} placeholder="Preferred date" onFocus={(e) => (e.target.type = "date")} onBlur={(e) => !e.target.value && (e.target.type = "text")} value={form.preferred_date} onChange={update("preferred_date")} />
                </div>
                <textarea data-testid="booking-message" rows={3} className={field} placeholder="Tell me about your horse and any concerns" value={form.message} onChange={update("message")} />
                <button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={loading}
                  className="self-start inline-flex items-center gap-2 bg-clay text-sand rounded-full px-8 py-4 font-medium transition-all duration-300 hover:bg-clay-dark hover:-translate-y-1 shadow-sm hover:shadow-md disabled:opacity-60"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} strokeWidth={1.5} />}
                  {loading ? "Sending..." : "Send request"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
